import React, { useRef, useState, useEffect } from "react";
import { FaCommentDots, FaPaperPlane, FaTimes } from "react-icons/fa";
import { chatWithAgent } from "./utils";
import { ThreeDots } from "react-loader-spinner";

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const chatsRef = useRef(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isBoatLoading, setIsBoatLoading] = useState(false);
  const chatbotWrapperRef = useRef(null);
  
  // Custom simple parser to avoid heavy npm dependencies
  const renderMarkdown = (text) => {
    if (!text) return { __html: "" };
    
    let html = text
      .replace(/^###\s+(.*$)/gim, '<h5 class="mt-3 mb-2" style="font-weight: 600;">$1</h5>')
      .replace(/^##\s+(.*$)/gim, '<h4 class="mt-3 mb-2" style="font-weight: 600;">$1</h4>')
      .replace(/^#\s+(.*$)/gim, '<h3 class="mt-3 mb-2" style="font-weight: 600;">$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\[FAQ:\s*(.+?)\]/g, (match, q) => `<br/><a href="/faq?q=${encodeURIComponent(q)}" target="_blank" rel="noopener noreferrer" style="color: #00d2ff; text-decoration: underline; font-weight: 500;">Read more in FAQ</a>`)
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #00d2ff; text-decoration: underline; font-weight: 500;">$1</a>')
      .replace(/\n\n/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');

    html = html.replace(/(\d+\.)\s/g, '<strong>$1</strong> ');
    html = html.replace(/(-\s)/g, '<strong>•</strong> ');

    return { __html: html };
  };

  const [messages, setMessages] = useState([
    {
      text: "Hello! How can I help you today? I'm here to assist.",
      isBot: true,
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");

  const handleSendMessage = async (e) => {
    e.preventDefault();

    try {
      setIsBoatLoading(true);
      if (!inputMessage.trim()) {
        return;
      }

      const newMessage = {
        text: inputMessage,
        isBot: false,
        timestamp: new Date(),
      };
      
      // We use the current messages to build history before adding the new user message.
      // But we also need to include the NEW user message in the current list.
      const updatedMessages = [...messages, newMessage];
      setMessages(updatedMessages);
      const question = inputMessage;
      setInputMessage("");

      // Map local messages state to OpenAI `{ role, content }` format.
      const history = messages.map(msg => ({
        role: msg.isBot ? "assistant" : "user",
        content: msg.text
      }));

      const chatMessage = await chatWithAgent(question, history);
      console.log("questionAns", { question, chatMessage });

      const newMessageBot = {
        text: chatMessage?.answer || chatMessage?.error || "Sorry, I couldn't process your request. Please try again.",
        isBot: true,
        boats: chatMessage?.boats || null,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, newMessageBot]);
      setIsBoatLoading(false);
    } catch (error) {
      console.error("Error sending message:", error);
      
      // Add error message to chat
      const errorMessage = {
        text: "Sorry, there was an error processing your request. Please try again later.",
        isBot: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsBoatLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        chatbotWrapperRef.current &&
        !chatbotWrapperRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setIsMinimized(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && chatsRef.current) {
      // Add a small delay to ensure the content is rendered
      const timer = setTimeout(() => {
        if (chatsRef.current) {
          chatsRef.current.scrollTo({
            top: chatsRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, messages, isBoatLoading]);

  return (
    <div
      ref={chatbotWrapperRef}
      className="chatbot-wrapper position-fixed p-3 d-flex flex-column align-items-end"
      style={{ zIndex: 999999999, bottom: "15px", right: "25px" }}
    >
      {isOpen && !isMinimized && (
        <div
          className="card shadow-lg"
          style={{ width: "380px", marginBottom: "16px" }}
        >
          {/* Header */}
          <div className="card-header bg-black text-white d-flex justify-content-between align-items-center p-3">
            <div className="d-flex align-items-center">
              <FaCommentDots className="text-white me-2" size={24} />
              <h5 className="mb-0">BBR Chatbot</h5>
            </div>
            <div className="d-flex align-items-center justify-content-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen((prev) => !prev);
                  setIsMinimized(false);
                }}
                className="btn btn-sm text-white border border-white rounded-circle p-1 me-2"
              >
                <FaTimes size={20} />
              </button>
            </div>
          </div>

          {/* Chat Messages */}
          <div
            ref={chatsRef}
            className="card-body overflow-auto p-3"
            style={{ height: "400px" }}
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`d-flex my-3 ${
                  message.isBot
                    ? "justify-content-start"
                    : "justify-content-end"
                }`}
              >
                <div
                  className={`p-2 rounded ${
                    message.isBot ? "bg-light text-dark" : "bg-black text-white"
                  }`}
                  style={{ maxWidth: "85%" }}
                >
                  {message.isBot ? (
                    <div 
                      className="mb-1 chatbot-markdown" 
                      style={{ fontSize: "14px", lineHeight: "1.5" }}
                      dangerouslySetInnerHTML={renderMarkdown(message.text)}
                    />
                  ) : (
                    <p className="mb-1" style={{ whiteSpace: "pre-wrap" }}>{message.text}</p>
                  )}
                  
                  {message.boats && message.boats.length > 0 && (
                    <div className="mt-3">
                      {message.boats.map((boat) => (
                        <div key={boat.id} className="card mb-3 shadow-sm border-0 bg-white" style={{ fontSize: "14px", overflow: "hidden", borderRadius: "12px" }}>
                           <div style={{ width: "100%", height: "140px", overflow: "hidden", position: "relative" }}>
                             <img 
                               src={boat.image} 
                               alt={boat.title} 
                               style={{ width: "100%", height: "100%", objectFit: "cover" }}
                             />
                             <div className="position-absolute bottom-0 start-0 w-100 p-2" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)" }}>
                               <span className="badge bg-primary">{boat.dailyRate}</span>
                             </div>
                           </div>
                           <div className="card-body p-3">
                             <h6 className="card-title fw-bold mb-1 text-dark text-truncate">{boat.title}</h6>
                             <div className="d-flex align-items-center text-muted mb-2" style={{ fontSize: "12px" }}>
                               <i className="fa-solid fa-location-dot me-1 text-danger"></i>
                               <span className="text-truncate">{boat.location}</span>
                             </div>
                             <p className="card-text mb-3 text-muted" style={{ fontSize: "12px" }}>
                               <span className="badge bg-light text-dark border">Type: {boat.type}</span>
                             </p>
                             <a href={boat.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm w-100 fw-bold" style={{ fontSize: "13px", borderRadius: "8px" }}>
                               View Details <i className="fa-solid fa-arrow-right ms-1"></i>
                             </a>
                           </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="mb-0 small text-opacity-75 mt-1 text-end">
                    {message.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
            ))}
            {isBoatLoading && (
              <div className={`d-flex my-3 justify-content-start`}>
                <div
                  className={`p-2 rounded bg-light text-dark`}
                  style={{ maxWidth: "70%" }}
                >
                  <p className="mb-1">
                    <ThreeDots color="black" width={50} height={30} />
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Input Area */}
          <form
            onSubmit={handleSendMessage}
            className="card-footer p-3 border-top"
          >
            <div className="input-group">
              <input
                disabled={isBoatLoading}
                type="text"
                value={inputMessage}
                onChange={(e) => {
                  e.stopPropagation();
                  setInputMessage(e.target.value);
                }}
                placeholder="Type your message..."
                className="form-control"
              />
              <button type="submit" className="btn btn-primary">
                <FaPaperPlane size={20} />
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Chat Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
          setIsMinimized(false);
        }}
        className="btn btn-primary border border-2 rounded-circle p-3 shadow-lg d-flex align-items-center justify-content-center"
      >
        <FaCommentDots className="text-white" size={24} />
      </button>
    </div>
  );
};
export default ChatBot;
